import { useApiQuery, useApiMutation, optimisticUpdate } from '@/hooks/use-api';
import { toast } from '@/components/ui/use-toast';

export interface User {
  id: string;
  _id?: string;
  name: string;
  email: string;
  surname: string;
  role: string;
  gymId?: string;
  createdAt: string;
  // Add other user fields
}

export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  role: string;
  // Add other fields
}

export interface UpdateUserData {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  // Add other fields
}

// Get all users (for dropdowns / dialogs that need the full list).
// Backend now always returns a paginated envelope; we unwrap it to a plain User[].
export function useUsers(gymId?: string) {
  const query = useApiQuery<PaginatedUsersResponse>(
    ['users'],
    {
      method: 'GET',
      url: '/users?userType=user&limit=1000',
    },
    { enabled: true, staleTime: 0 }
  );

  const data: User[] = query.data
    ? Array.isArray(query.data)
      ? (query.data as unknown as User[])
      : (query.data.results ?? [])
    : [];

  return { ...query, data };
}

export interface PaginatedUsersParams {
  search?: string;
  page?: number;
  limit?: number;
  userType?: string;
  filter?: string;
}

export interface PaginatedUsersResponse {
  results: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Get users with server-side pagination and search
export function useUsersPaginated(params: PaginatedUsersParams) {
  const {
    search = '',
    page = 1,
    limit = 25,
    userType = 'user',
    filter,
  } = params;
  const searchParams = new URLSearchParams({
    userType,
    page: String(page),
    limit: String(limit),
  });
  if (search.trim()) {
    searchParams.set('search', search.trim());
  }
  if (filter) {
    searchParams.set('filter', filter);
  }
  return useApiQuery<PaginatedUsersResponse>(
    ['users', 'paginated', userType, page, limit, search, filter],
    {
      method: 'GET',
      url: `/users?${searchParams.toString()}`,
    },
    {
      enabled: true,
      staleTime: 0,
    }
  );
}

// Get a single user
export function useUser(id: string) {
  return useApiQuery<User>(
    ['users', id],
    { method: 'GET', url: `/users/${id}` },
    {
      enabled: !!id,
    }
  );
}

// Create a user
export function useCreateUser() {
  return useApiMutation<User, CreateUserData>(
    (data) => ({
      method: 'POST',
      url: '/users',
      data: { ...data, userType: 'user' },
    }),
    {
      onSuccess: (data, variables, context) => {
        toast({
          title: 'Success',
          description: 'User created successfully',
        });
      },
      // Invalidate the users list query when a new user is created
      onSettled: (data, error, variables, context) => {
        // Invalidate all users queries
        return [['users']];
      },
    }
  );
}

// Update a user
export function useUpdateUser() {
  return useApiMutation<User, UpdateUserData>(
    (data) => ({
      method: 'PUT',
      url: `/users/${data.id}`,
      data: { ...data, userType: 'user' },
    }),
    {
      ...optimisticUpdate({
        queryKey: ['users', 'id'],
        updateFn: (old, variables) => {
          if (!old) return old;
          return { ...old, ...variables };
        },
      }),
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'User updated successfully',
        });
      },
      // Invalidate the users list query when a user is updated
      onSettled: (data, error, variables, context) => {
        // Invalidate all users queries
        return [['users']];
      },
    }
  );
}

// Delete a user
export function useDeleteUser() {
  return useApiMutation<void, string>(
    (id) => ({
      method: 'DELETE',
      url: `/users/${id}`,
    }),
    {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'User deleted successfully',
        });
      },
      onError: (error) => {
        console.error('Error deleting user:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete user. Please try again.',
          variant: 'destructive',
        });
      },
      // Invalidate the users list query when a user is deleted
      onSettled: (data, error, variables, context) => {
        // Invalidate all users queries
        return [['users']];
      },
    }
  );
}
